import { Box } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { btnSx } from "../components/ActionDrawer";
import {
  CardGrid,
  Empty,
  GridCard,
  Notice,
  PageHead,
  Tag,
} from "../components/DashTable";
import PageLoader from "../components/Loader";
import { useToast } from "../components/Toast";
import apiService from "../Services/api.service";

const daysSince = (iso) => {
  if (!iso) return null;
  const diff = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(diff)) return null;
  return Math.floor(diff / (1000 * 60 * 60 * 24));
};

/* PG1. Incomplete wizards. Nothing here has been seen by a customer. */
const Drafts = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const token = localStorage.getItem("token");

  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [discardingId, setDiscardingId] = useState(null);

  const fetchRows = async () => {
    setIsLoading(true);
    setLoadFailed(false);
    try {
      const res = await apiService.fetchDrafts(token);
      setRows(res.data || []);
      setSummary(res.summary || null);
    } catch (error) {
      setLoadFailed(true);
      toast.error("Error while fetching drafts");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDiscard = async (row) => {
    setDiscardingId(row.orderId);
    try {
      await apiService.discardDraft(row.orderId, token);
      toast.success("Draft discarded");
      setRows((prev) => prev.filter((r) => r.orderId !== row.orderId));
    } catch (error) {
      toast.error(error.response?.data?.message || "Could not discard draft");
    } finally {
      setDiscardingId(null);
    }
  };

  const kpis = summary
    ? [
        { label: "Drafts", value: summary.count ?? rows.length },
        { label: "Oldest", value: summary.oldestDays ? `${summary.oldestDays}d` : "—" },
      ]
    : [];

  return (
    <Box sx={{ padding: { xs: "18px 16px 50px", md: "24px 28px 60px" }, maxWidth: 1240 }}>
      <PageHead
        title="Drafts"
        lede="Incomplete wizards. Nothing here has been seen by a customer."
        kpis={kpis}
      />

      {loadFailed ? (
        <Empty
          title="Could not load drafts"
          sub={
            <Box
              component="button"
              type="button"
              onClick={fetchRows}
              sx={{ ...btnSx("ghost"), margin: "12px auto 0", display: "block" }}
            >
              Retry
            </Box>
          }
        />
      ) : rows.length === 0 && !isLoading ? (
        <Empty
          title="No drafts"
          sub="An unfinished new-order wizard appears here."
        />
      ) : (
        <CardGrid>
          {rows.map((row) => (
            <GridCard
              key={row.orderId}
              title={row.clinicName || "Untitled draft"}
              tag={
                <Tag tone="i">
                  Step {row.step ?? "?"} of {row.ofSteps ?? 4}
                </Tag>
              }
              lines={
                <>
                  {row.doctorName}
                  <br />
                  {row.city}
                  <br />
                  Saved {daysSince(row.savedAt) ?? "—"} days ago
                </>
              }
              actions={
                <>
                  <Box
                    component="button"
                    type="button"
                    onClick={() =>
                      navigate(`/dashboard/orders/add-order?draft=${row.orderId}`)
                    }
                    sx={btnSx("ghost")}
                  >
                    Resume
                  </Box>
                  <Box
                    component="button"
                    type="button"
                    onClick={() => handleDiscard(row)}
                    disabled={discardingId === row.orderId}
                    sx={btnSx("ghost")}
                  >
                    {discardingId === row.orderId ? "Discarding" : "Discard"}
                  </Box>
                </>
              }
            />
          ))}
        </CardGrid>
      )}

      <Notice>
        <b>Open decision.</b> Drafts have no expiry. Decide how long an
        abandoned wizard survives before it is purged.
      </Notice>

      {isLoading && <PageLoader />}
    </Box>
  );
};

export default Drafts;
